import React from 'react'
import {
    InputAdornment as MuiInputAdornment,
    TextField as MuiTextField,
    TextFieldProps as MuiTextFieldProps,
} from '@mui/material'
import { FieldValues, useController, UseControllerProps } from 'react-hook-form'
import { NumericFormat, type NumericFormatProps } from 'react-number-format'
import { parseFieldError } from './utils'

// FORMAT: "$ 1,234,567.89"

type Props<FormValues extends FieldValues = FieldValues> = NumericFormatProps &
    MuiTextFieldProps &
    UseControllerProps<FormValues> & {
        valueAsNumber?: boolean
    }

export const CurrencyFieldInputRef = <FormValues extends FieldValues = FieldValues>({
    name,
    control,
    defaultValue,
    rules,
    shouldUnregister,
    ...restInputProps
}: Props<FormValues>): JSX.Element => {
    const {
        field: { ref: fieldRef, ...rhfFieldProps },
        fieldState: {
            // invalid,
            // isTouched,
            // isDirty,
            error,
        },
    } = useController<FormValues>({
        name,
        control, // given if nested in `FormProvider`
        defaultValue,
        rules,
        shouldUnregister,
    })

    const onChange: NumericFormatProps['onChange'] = (e) => {
        const removedCommas = e.target.value.replaceAll(',', '')
        if (restInputProps.onChange) {
            e.target.value = removedCommas
            restInputProps.onChange(e)
        }

        if (!restInputProps.valueAsNumber) {
            rhfFieldProps.onChange(removedCommas)
        }

        const asNumber = +removedCommas
        rhfFieldProps.onChange(asNumber)
    }

    return (
        <NumericFormat
            // @ts-expect-error TODO: Fix types
            customInput={MuiTextField}
            thousandSeparator
            decimalScale={2}
            fixedDecimalScale={!Number.isInteger(rhfFieldProps.value)}
            inputRef={fieldRef}
            {...restInputProps}
            {...rhfFieldProps}
            onChange={onChange}
            value={
                restInputProps.value === 0 || rhfFieldProps.value === 0
                    ? 0
                    : restInputProps.value || rhfFieldProps.value || ''
            }
            required={restInputProps.required || !!rules?.required}
            error={restInputProps.error || !!error}
            helperText={restInputProps.helperText || parseFieldError(error)}
            InputProps={{
                startAdornment: <MuiInputAdornment position="start">$</MuiInputAdornment>,
                ...restInputProps.InputProps,
            }}
            // InputProps!==inputProps
            // eslint-disable-next-line react/jsx-no-duplicate-props
            inputProps={{
                inputMode: 'decimal',
                pattern: '[0-9,]*',
                ...restInputProps.inputProps,
            }}
        />
    )
}

export default CurrencyFieldInputRef
export { CurrencyFieldInputRef as MuiRhfCurrencyField, CurrencyFieldInputRef as CurrencyField }
export type { Props as CurrencyFieldProps, Props as MuiRhfCurrencyFieldProps }
